import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import DownloadIcon from "@mui/icons-material/Download";
import { PageHeader } from "@/components/common/PageHeader";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { downloadAuditCsv, fetchAuditLogs } from "@/api/audit";
import { fetchUsers } from "@/api/settings";
import { useAuth } from "@/auth/useAuth";
import { ROLE_LABELS } from "@/types/enums";
import { formatDateTime } from "@/utils/format";

// Действия пишутся глобальным AuditInterceptor как «HTTP-метод путь» (ТЗ §8) —
// приводим к читаемым названиям вроде «Создание»/«Изменение»/«Удаление» из прототипа (ACTION_NAMES).
const METHOD_LABELS: Record<string, string> = { POST: "Создание", PATCH: "Изменение", PUT: "Изменение", DELETE: "Удаление" };
const ENTITY_LABELS: Record<string, string> = {
  students: "Ученик",
  groups: "Группа",
  teachers: "Преподаватель",
  payments: "Оплата",
  expenses: "Расход",
  leads: "Заявка",
  users: "Пользователь",
  templates: "Шаблон",
  lessons: "Урок",
  notifications: "Уведомление",
  registrations: "Регистрация",
  settings: "Настройки",
  branches: "Филиал",
  billing: "Финансы",
  finance: "Финансы",
};

function humanizeAction(action: string): string {
  const [method] = action.split(" ", 1);
  return METHOD_LABELS[method] ?? action;
}

function humanizeEntity(entityType: string): string {
  return ENTITY_LABELS[entityType] ?? entityType;
}

const EMPTY_FILTERS = { q: "", userId: "", action: "", entityType: "", from: "", to: "" };

export function AuditPage() {
  const { hasPermission } = useAuth();
  const canListUsers = hasPermission("set.users");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const { data: users } = useQuery({
    queryKey: ["users", "for-audit"],
    queryFn: () => fetchUsers({ limit: 200 }),
    enabled: canListUsers,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["audit", filters],
    queryFn: () => fetchAuditLogs({
      q: filters.q || undefined,
      userId: filters.userId || undefined,
      action: filters.action || undefined,
      entityType: filters.entityType || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      limit: 100,
    }),
  });

  const rows = data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Аудит действий"
        subtitle="Журнал изменений в CRM"
        actions={<Button startIcon={<DownloadIcon />} variant="outlined" onClick={() => downloadAuditCsv(filters)}>CSV</Button>}
      />
      <Paper className="crm-page-filter" variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            size="small"
            label="Поиск по ID сущности"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
          />
          {canListUsers && (
            <TextField select size="small" label="Пользователь" value={filters.userId} onChange={(e) => setFilters({ ...filters, userId: e.target.value })} sx={{ minWidth: 180 }}>
              <MenuItem value="">Все пользователи</MenuItem>
              {(users?.items ?? []).map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.fullName} · {ROLE_LABELS[u.role]}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField select size="small" label="Действие" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} sx={{ minWidth: 150 }}>
            <MenuItem value="">Все действия</MenuItem>
            {Object.entries(METHOD_LABELS).map(([method, label]) => (
              <MenuItem key={method} value={method}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Сущность" value={filters.entityType} onChange={(e) => setFilters({ ...filters, entityType: e.target.value })} sx={{ minWidth: 160 }}>
            <MenuItem value="">Все сущности</MenuItem>
            {Object.entries(ENTITY_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="С" type="date" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
          <TextField size="small" label="По" type="date" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
          <Button size="small" onClick={() => setFilters(EMPTY_FILTERS)}>
            Сбросить
          </Button>
        </Stack>
      </Paper>
      <Typography variant="caption" color="text.secondary" mb={1} display="block">
        Найдено записей: <b>{data?.total ?? 0}</b>
      </Typography>
      <Paper className="crm-data-panel" variant="outlined">
        <QueryState isLoading={isLoading} error={error}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Когда</TableCell>
                <TableCell>Кто</TableCell>
                <TableCell>Роль</TableCell>
                <TableCell>Действие</TableCell>
                <TableCell>Сущность</TableCell>
                <TableCell>ID</TableCell>
                <TableCell>IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                  <TableCell>
                    {row.userName ?? "—"}
                    <br />
                    <Typography component="span" variant="caption" color="text.secondary">
                      {row.userLogin}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.userRole ? <StatusChip label={ROLE_LABELS[row.userRole as keyof typeof ROLE_LABELS] ?? row.userRole} /> : "—"}</TableCell>
                  <TableCell>
                    <b>{humanizeAction(row.action)}</b>
                  </TableCell>
                  <TableCell>{humanizeEntity(row.entityType)}</TableCell>
                  <TableCell>{row.entityId ?? "—"}</TableCell>
                  <TableCell>{row.ip ?? "—"}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Записей нет.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </QueryState>
      </Paper>
    </>
  );
}
